import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { matchesAPI } from '../services/api';

const ERROR_TYPES = ['out', 'net', 'shank', 'net_fault', 'uncertain'];

function ErrorReviewPanel({ matchId, currentUser }) {
  const [errors, setErrors] = useState([]);
  const [currentErrorIndex, setCurrentErrorIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [selectedCorrection, setSelectedCorrection] = useState(null);
  const [correctionReason, setCorrectionReason] = useState('');
  const [errorStats, setErrorStats] = useState(null);
  const [showStats, setShowStats] = useState(false);

  useEffect(() => {
    fetchErrorData();
  }, [matchId]);

  const fetchErrorData = async () => {
    try {
      setLoading(true);
      const errorsData = await matchesAPI.getErrors(matchId, false); // Get unverified errors
      setErrors(errorsData.errors || []);

      // Also fetch statistics
      const statsData = await matchesAPI.getErrorStats(matchId);
      setErrorStats(statsData);
    } catch (error) {
      console.error('Failed to fetch error data:', error);
      toast.error('Failed to load error data');
    } finally {
      setLoading(false);
    }
  };

  const handleCorrectError = async (errorId, correctedType) => {
    try {
      await matchesAPI.correctError(matchId, errorId, correctedType, true, correctionReason);

      // Remove corrected error from list
      setErrors(errors.filter(e => e.id !== errorId));

      // Reset form
      setCorrectionReason('');
      setSelectedCorrection(null);

      // Move to next error
      if (currentErrorIndex >= errors.length - 1 && currentErrorIndex > 0) {
        setCurrentErrorIndex(currentErrorIndex - 1);
      }

      // Refresh statistics
      const statsData = await matchesAPI.getErrorStats(matchId);
      setErrorStats(statsData);

      toast.success('Error corrected');
    } catch (error) {
      console.error('Failed to correct error:', error);
      toast.error('Failed to correct error');
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const getConfidenceColor = (confidence) => {
    if (confidence >= 0.7) return '#10b981'; // green
    if (confidence >= 0.4) return '#f59e0b'; // amber
    return '#ef4444'; // red
  };

  if (loading) {
    return <div className="loading">Loading error data...</div>;
  }

  const currentError = errors[currentErrorIndex];

  return (
    <div>
      {/* Error Statistics */}
      {errorStats && (
        <div className="card" style={{ marginBottom: '1rem', background: '#f3f4f6' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <h4 style={{ marginBottom: '0.5rem' }}>Error Summary</h4>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '1rem' }}>
                <div>
                  <div style={{ fontSize: '0.875rem', color: '#64748b' }}>Total Errors</div>
                  <div style={{ fontSize: '1.5rem', fontWeight: 'bold' }}>{errorStats.total_errors}</div>
                </div>
                <div>
                  <div style={{ fontSize: '0.875rem', color: '#64748b' }}>Unverified</div>
                  <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#ef4444' }}>
                    {errorStats.unverified_errors}
                  </div>
                </div>
                <div>
                  <div style={{ fontSize: '0.875rem', color: '#64748b' }}>Avg Confidence</div>
                  <div style={{ fontSize: '1.5rem', fontWeight: 'bold' }}>
                    {(errorStats.average_confidence * 100).toFixed(0)}%
                  </div>
                </div>
              </div>
            </div>
            <button
              onClick={() => setShowStats(!showStats)}
              className="btn btn-secondary"
              style={{ alignSelf: 'flex-start' }}
            >
              {showStats ? '▼' : '▶'} Breakdown
            </button>
          </div>

          {showStats && errorStats.errors_by_type && (
            <div style={{ marginTop: '1rem', paddingTop: '1rem', borderTop: '1px solid #cbd5e1' }}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '1rem' }}>
                {Object.entries(errorStats.errors_by_type).map(([type, count]) => (
                  <div key={type} style={{ padding: '0.75rem', background: '#ffffff', borderRadius: '4px' }}>
                    <div style={{ fontSize: '0.875rem', color: '#64748b', textTransform: 'capitalize' }}>{type}</div>
                    <div style={{ fontSize: '1.25rem', fontWeight: 'bold' }}>{count}</div>
                  </div>
                ))}
              </div>
              <div style={{ marginTop: '1rem' }}>
                <div style={{ fontSize: '0.875rem', color: '#64748b', marginBottom: '0.5rem' }}>
                  Confidence Distribution
                </div>
                <div style={{ display: 'flex', gap: '1rem', fontSize: '0.875rem' }}>
                  <div>
                    <span style={{ color: '#10b981' }}>●</span> High (≥0.7): {errorStats.confidence_distribution.high}
                  </div>
                  <div>
                    <span style={{ color: '#f59e0b' }}>●</span> Medium (0.4-0.7): {errorStats.confidence_distribution.medium}
                  </div>
                  <div>
                    <span style={{ color: '#ef4444' }}>●</span> Low (&lt;0.4): {errorStats.confidence_distribution.low}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {errors.length === 0 ? (
        <div style={{ padding: '3rem', textAlign: 'center', color: '#64748b', background: '#f9fafb', borderRadius: '4px' }}>
          <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>✅</div>
          <h3>No errors to review</h3>
          <p>All detected errors have been verified.</p>
        </div>
      ) : currentError ? (
        <div className="card">
          {/* Error Navigation */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
            <div>
              <h3>Error Review ({errors.length} unverified)</h3>
              <div style={{ color: '#64748b', fontSize: '0.875rem', marginTop: '0.5rem' }}>
                Error #{currentErrorIndex + 1} of {errors.length}
              </div>
            </div>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <button
                onClick={() => setCurrentErrorIndex(Math.max(0, currentErrorIndex - 1))}
                className="btn btn-secondary"
                disabled={currentErrorIndex === 0}
              >
                ← Prev
              </button>
              <button
                onClick={() => setCurrentErrorIndex(Math.min(errors.length - 1, currentErrorIndex + 1))}
                className="btn btn-secondary"
                disabled={currentErrorIndex >= errors.length - 1}
              >
                Next →
              </button>
            </div>
          </div>

          {/* Error Details */}
          <div style={{ padding: '1rem', background: '#f9fafb', borderRadius: '4px', marginBottom: '1rem' }}>
            <div style={{ marginBottom: '0.75rem' }}>
              <strong>Error Type (Detected):</strong> {currentError.error_type}
            </div>
            <div style={{ marginBottom: '0.75rem' }}>
              <strong>Confidence:</strong>{' '}
              <span
                style={{
                  color: getConfidenceColor(currentError.confidence),
                  fontWeight: 'bold',
                }}
              >
                {(currentError.confidence * 100).toFixed(0)}%
              </span>
            </div>
            <div style={{ marginBottom: '0.75rem' }}>
              <strong>Time:</strong> {formatTime(currentError.timestamp)}
            </div>
            <div style={{ marginBottom: '0.75rem' }}>
              <strong>Detection Method:</strong> {currentError.detection_method}
            </div>
            {currentError.details && Object.keys(currentError.details).length > 0 && (
              <div>
                <strong>Details:</strong>
                <div style={{ fontSize: '0.875rem', color: '#64748b', marginTop: '0.25rem' }}>
                  {JSON.stringify(currentError.details, null, 2)}
                </div>
              </div>
            )}
          </div>

          {/* Error Correction Form */}
          <div style={{ padding: '1rem', background: '#eff6ff', borderRadius: '4px', marginBottom: '1rem' }}>
            <strong>Correct Error Type (if AI was wrong):</strong>
            <div style={{ marginTop: '0.5rem' }}>
              <select
                value={selectedCorrection || ''}
                onChange={(e) => setSelectedCorrection(e.target.value)}
                style={{
                  width: '100%',
                  padding: '0.5rem',
                  borderRadius: '4px',
                  border: '1px solid #cbd5e1',
                  marginBottom: '0.5rem',
                }}
              >
                <option value="">-- Keep as {currentError.error_type} --</option>
                {ERROR_TYPES.map((type) => (
                  <option key={type} value={type} disabled={type === currentError.error_type}>
                    {type}
                  </option>
                ))}
              </select>

              {selectedCorrection && (
                <>
                  <textarea
                    value={correctionReason}
                    onChange={(e) => setCorrectionReason(e.target.value)}
                    placeholder="Why are you correcting this? (optional)"
                    style={{
                      width: '100%',
                      padding: '0.5rem',
                      borderRadius: '4px',
                      border: '1px solid #cbd5e1',
                      marginBottom: '0.5rem',
                      fontFamily: 'inherit',
                    }}
                    rows={2}
                  />
                  <button
                    onClick={() => handleCorrectError(currentError.id, selectedCorrection)}
                    className="btn btn-primary"
                    style={{ width: '100%' }}
                  >
                    ✓ Confirm Correction
                  </button>
                </>
              )}
            </div>
          </div>

          {/* Verify As Correct */}
          {!selectedCorrection && (
            <button
              onClick={() => handleCorrectError(currentError.id, currentError.error_type)}
              className="btn btn-success"
              style={{ width: '100%' }}
            >
              ✓ Mark as Correct
            </button>
          )}
        </div>
      ) : null}
    </div>
  );
}

export default ErrorReviewPanel;
